import DOMPurify from 'dompurify';
import {match} from 'ts-pattern';

import {collapseQuotedReplies} from '@dms/shared';

import {ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS} from '@prompt-bridge/shared/ai-assistant-core';

const COLLAPSED_QUOTE_SELECTOR = 'details.omnetic-quote';
const HIDDEN_CONTENT_SELECTOR = '[hidden]';
const NON_VISIBLE_CONTENT_SELECTOR = 'iframe, link, meta, noscript, script, style, template, title';
const IMPORTANT_SUFFIX = '!important';
const QUOTED_REPLY_LABEL = 'Quoted reply';
const TEXT_BOUNDARY_SELECTOR =
  'address, article, aside, blockquote, br, dd, div, dl, dt, fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, header, hr, li, main, nav, ol, p, pre, section, table, tbody, td, tfoot, th, thead, tr, ul';
const WHITESPACE_RUN = /\s+/g;

export type AssistantBusinessCaseExcerpt = {text: string; truncated: boolean};

function isHiddenStyleDeclaration(declaration: string) {
  const separatorIndex = declaration.indexOf(':');
  if (separatorIndex === -1) {
    return false;
  }

  const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
  const normalizedValue = declaration
    .slice(separatorIndex + 1)
    .trim()
    .toLowerCase();
  const value = normalizedValue.endsWith(IMPORTANT_SUFFIX)
    ? normalizedValue.slice(0, -IMPORTANT_SUFFIX.length).trim()
    : normalizedValue;

  return match({property, value})
    .with({property: 'display', value: 'none'}, () => true)
    .with({property: 'visibility', value: 'hidden'}, () => true)
    .with({property: 'opacity'}, ({value: opacity}) => opacity.length > 0 && Number(opacity) === 0)
    .with({property: 'font-size'}, ({value: fontSize}) => fontSize === '0' || fontSize === '0px')
    .with({property: 'text-indent'}, ({value: textIndent}) => textIndent.startsWith('-'))
    .with({property: 'clip'}, () => true)
    .with({property: 'clip-path'}, () => true)
    .otherwise(() => false);
}

function hasHiddenInlineStyle(style: string) {
  return style.split(';').some(isHiddenStyleDeclaration);
}

export function toAssistantBusinessCaseExcerpt(html: string): AssistantBusinessCaseExcerpt | null {
  const sanitized = DOMPurify.sanitize(html);
  const collapsed = collapseQuotedReplies(sanitized, QUOTED_REPLY_LABEL);
  const document = new DOMParser().parseFromString(collapsed, 'text/html');
  document
    .querySelectorAll(
      `${COLLAPSED_QUOTE_SELECTOR}, ${HIDDEN_CONTENT_SELECTOR}, ${NON_VISIBLE_CONTENT_SELECTOR}`
    )
    .forEach((element) => element.remove());
  document.querySelectorAll('[style]').forEach((element) =>
    match(element.getAttribute('style'))
      .when(
        (style) => typeof style === 'string' && hasHiddenInlineStyle(style),
        () => element.remove()
      )
      .otherwise(() => undefined)
  );
  document.querySelectorAll(TEXT_BOUNDARY_SELECTOR).forEach((element) => element.after(' '));
  const text = (document.body.textContent ?? '').replace(WHITESPACE_RUN, ' ').trim();

  return match(text)
    .returnType<AssistantBusinessCaseExcerpt | null>()
    .with('', () => null)
    .otherwise((nonEmptyText) => ({
      text: nonEmptyText.slice(0, ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS),
      truncated: nonEmptyText.length > ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS,
    }));
}
